import { createSlice } from '@reduxjs/toolkit';

// Import legacy action types for extraReducers bridge
import {
  SET_AREA_STATUS,
  SET_AREA_DEFERRED,
  SET_AREA_NOT_APPLICABLE,
  CLEAR_AREA_STATUS,
  UPDATE_SECTION_PROGRESS,
} from '../actions/actionTypes.js';
import { fetchAllAssessments, fetchSingleAssessment } from './assessmentsSlice';

// ============================================================================
// Assessment Progress Slice
// Manages area-level progress and status tracking for assessments
// ============================================================================

/**
 * State Shape:
 * {
 *   byAssessment: {
 *     [assessmentUuid]: {
 *       [areaName]: {
 *         status: 'IN_PROGRESS' | 'COMPLETE' | 'DEFERRED' | 'NA',
 *         progress: number (0-100),
 *         deferredAt?: string (ISO timestamp),
 *         markedNAAt?: string (ISO timestamp)
 *       },
 *       _checkpoint: {
 *         pausedAt?: string (ISO timestamp),
 *         currentArea?: string,
 *         currentAreaIndex?: number,
 *         phase?: string,
 *         completedAreas?: string[],
 *         majorStep?: string,
 *         uploaded?: { bucket: string, path: string },
 *         narrativesApproved?: boolean,
 *         savedAt?: string (ISO timestamp),
 *         resumedAt?: string (ISO timestamp),
 *         resumeCount?: number
 *       }
 *     }
 *   }
 * }
 */

const initialState = {
  byAssessment: {},
};

const assessmentProgressSlice = createSlice({
  name: 'assessmentProgress',
  initialState,
  reducers: {
    // RTK action creators for new code
    setAreaStatus: (state, action) => {
      const { assessmentUuid, areaName, status } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][areaName]) {
        state.byAssessment[assessmentUuid][areaName] = { progress: 0 };
      }
      state.byAssessment[assessmentUuid][areaName].status = status;
      // If deferred or NA, mark as 100% complete for progress tracking
      if (status === 'DEFERRED' || status === 'NA') {
        state.byAssessment[assessmentUuid][areaName].progress = 100;
      }
    },

    setAreaDeferred: (state, action) => {
      const { assessmentUuid, areaName } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][areaName]) {
        state.byAssessment[assessmentUuid][areaName] = {};
      }
      state.byAssessment[assessmentUuid][areaName].status = 'DEFERRED';
      state.byAssessment[assessmentUuid][areaName].progress = 100;
      state.byAssessment[assessmentUuid][areaName].deferredAt = new Date().toISOString();
    },

    setAreaNotApplicable: (state, action) => {
      const { assessmentUuid, areaName } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][areaName]) {
        state.byAssessment[assessmentUuid][areaName] = {};
      }
      state.byAssessment[assessmentUuid][areaName].status = 'NA';
      state.byAssessment[assessmentUuid][areaName].progress = 100;
      state.byAssessment[assessmentUuid][areaName].markedNAAt = new Date().toISOString();
    },

    clearAreaStatus: (state, action) => {
      const { assessmentUuid, areaName } = action.payload;
      if (state.byAssessment[assessmentUuid]?.[areaName]) {
        // Remove status and timestamps, reset progress
        delete state.byAssessment[assessmentUuid][areaName].status;
        delete state.byAssessment[assessmentUuid][areaName].deferredAt;
        delete state.byAssessment[assessmentUuid][areaName].markedNAAt;
        state.byAssessment[assessmentUuid][areaName].progress = 0;
      }
    },

    updateAreaProgress: (state, action) => {
      const { assessmentUuid, areaName, progress } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][areaName]) {
        state.byAssessment[assessmentUuid][areaName] = {};
      }
      state.byAssessment[assessmentUuid][areaName].progress = progress;
    },

    // Clear all progress for an assessment (e.g., on assessment delete)
    clearAssessmentProgress: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    },

    // ============================================================================
    // Checkpoint Actions for Pause/Resume
    // ============================================================================

    // Save checkpoint when pausing assessment
    saveCheckpoint: (state, action) => {
      const { assessmentUuid, checkpoint } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      state.byAssessment[assessmentUuid]._checkpoint = {
        ...checkpoint,
        savedAt: new Date().toISOString()
      };
    },

    // Mark checkpoint as resumed (increments resume count)
    markResumed: (state, action) => {
      const { assessmentUuid } = action.payload;
      const checkpoint = state.byAssessment[assessmentUuid]?._checkpoint;
      if (checkpoint) {
        checkpoint.resumedAt = new Date().toISOString();
        checkpoint.resumeCount = (checkpoint.resumeCount || 0) + 1;
      }
    },

    // Clear checkpoint after completion or manual clear
    clearCheckpoint: (state, action) => {
      const { assessmentUuid } = action.payload;
      if (state.byAssessment[assessmentUuid]) {
        delete state.byAssessment[assessmentUuid]._checkpoint;
      }
    },
  },

  extraReducers: (builder) => {
    // ============================================================================
    // Legacy Action Type Bridge
    // These handlers listen to legacy action types dispatched from components
    // during the migration period. They update RTK state in parallel with riskReducer.
    // ============================================================================

    builder.addCase(SET_AREA_STATUS, (state, action) => {
      const { assessmentUuid, areaName, status } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][areaName]) {
        state.byAssessment[assessmentUuid][areaName] = { progress: 0 };
      }
      state.byAssessment[assessmentUuid][areaName].status = status;
      if (status === 'DEFERRED' || status === 'NA') {
        state.byAssessment[assessmentUuid][areaName].progress = 100;
      }
    });

    builder.addCase(SET_AREA_DEFERRED, (state, action) => {
      const { assessmentUuid, areaName } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][areaName]) {
        state.byAssessment[assessmentUuid][areaName] = {};
      }
      state.byAssessment[assessmentUuid][areaName].status = 'DEFERRED';
      state.byAssessment[assessmentUuid][areaName].progress = 100;
      state.byAssessment[assessmentUuid][areaName].deferredAt = new Date().toISOString();
    });

    builder.addCase(SET_AREA_NOT_APPLICABLE, (state, action) => {
      const { assessmentUuid, areaName } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][areaName]) {
        state.byAssessment[assessmentUuid][areaName] = {};
      }
      state.byAssessment[assessmentUuid][areaName].status = 'NA';
      state.byAssessment[assessmentUuid][areaName].progress = 100;
      state.byAssessment[assessmentUuid][areaName].markedNAAt = new Date().toISOString();
    });

    builder.addCase(CLEAR_AREA_STATUS, (state, action) => {
      const { assessmentUuid, areaName } = action.payload;
      if (state.byAssessment[assessmentUuid]?.[areaName]) {
        delete state.byAssessment[assessmentUuid][areaName].status;
        delete state.byAssessment[assessmentUuid][areaName].deferredAt;
        delete state.byAssessment[assessmentUuid][areaName].markedNAAt;
        state.byAssessment[assessmentUuid][areaName].progress = 0;
      }
    });

    builder.addCase(UPDATE_SECTION_PROGRESS, (state, action) => {
      const { assessmentUuid, sectionName, progress } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][sectionName]) {
        state.byAssessment[assessmentUuid][sectionName] = {};
      }
      state.byAssessment[assessmentUuid][sectionName].progress = progress;
    });

    // Rehydrate checkpoint from DB on assessment fetch. The autopilot reads
    // _checkpoint via selectResumeData; without this the slice is empty
    // after a page refresh and resume falls back to a fresh run.
    const hydrateCheckpoint = (state, assessment) => {
      if (!assessment?.assessment_uuid) return;
      const uuid = assessment.assessment_uuid;
      const checkpoint = assessment?.position?.checkpoint;
      if (!checkpoint) return;
      if (!state.byAssessment[uuid]) {
        state.byAssessment[uuid] = {};
      }
      // Don't clobber a live in-memory checkpoint with a stale fetched one.
      const existing = state.byAssessment[uuid]._checkpoint;
      if (existing?.savedAt && checkpoint?.savedAt && existing.savedAt >= checkpoint.savedAt) return;
      state.byAssessment[uuid]._checkpoint = checkpoint;
    };

    builder.addCase(fetchAllAssessments.fulfilled, (state, action) => {
      const assessments = action.payload || [];
      assessments.forEach((a) => hydrateCheckpoint(state, a));
    });

    builder.addCase(fetchSingleAssessment.fulfilled, (state, action) => {
      hydrateCheckpoint(state, action.payload);
    });
  },
});

export const {
  setAreaStatus,
  setAreaDeferred,
  setAreaNotApplicable,
  clearAreaStatus,
  updateAreaProgress,
  clearAssessmentProgress,
  // Checkpoint actions
  saveCheckpoint,
  markResumed,
  clearCheckpoint,
} = assessmentProgressSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectAssessmentProgress = (state) => state.assessmentProgress.byAssessment;

export const selectAreaProgress = (state, assessmentUuid, areaName) =>
  state.assessmentProgress.byAssessment[assessmentUuid]?.[areaName];

export const selectAreaStatus = (state, assessmentUuid, areaName) =>
  state.assessmentProgress.byAssessment[assessmentUuid]?.[areaName]?.status;

export const selectAllAreasForAssessment = (state, assessmentUuid) =>
  state.assessmentProgress.byAssessment[assessmentUuid] || {};

/**
 * Calculate overall assessment progress as average of all areas
 */
export const selectOverallProgress = (state, assessmentUuid) => {
  const areas = state.assessmentProgress.byAssessment[assessmentUuid];
  if (!areas) return 0;

  // Filter out _checkpoint (internal state, not an area)
  const areaNames = Object.keys(areas).filter(name => !name.startsWith('_'));
  if (areaNames.length === 0) return 0;

  const totalProgress = areaNames.reduce((sum, name) => {
    return sum + (areas[name]?.progress || 0);
  }, 0);

  return Math.round(totalProgress / areaNames.length);
};

/**
 * Get count of areas by status
 */
export const selectAreaStatusCounts = (state, assessmentUuid) => {
  const areas = state.assessmentProgress.byAssessment[assessmentUuid];
  if (!areas) return { complete: 0, inProgress: 0, deferred: 0, notApplicable: 0 };

  return Object.values(areas).reduce((counts, area) => {
    // Skip _checkpoint (it's not an area)
    if (typeof area !== 'object' || area === null) return counts;
    switch (area?.status) {
      case 'COMPLETE':
        counts.complete++;
        break;
      case 'IN_PROGRESS':
        counts.inProgress++;
        break;
      case 'DEFERRED':
        counts.deferred++;
        break;
      case 'NA':
        counts.notApplicable++;
        break;
      default:
        // No status yet
        break;
    }
    return counts;
  }, { complete: 0, inProgress: 0, deferred: 0, notApplicable: 0 });
};

// ============================================================================
// Checkpoint Selectors for Pause/Resume
// ============================================================================

/**
 * Get checkpoint for an assessment
 */
export const selectCheckpoint = (state, assessmentUuid) =>
  state.assessmentProgress.byAssessment[assessmentUuid]?._checkpoint || null;

/**
 * Check if assessment is paused (has a checkpoint with pausedAt)
 */
export const selectIsPaused = (state, assessmentUuid) =>
  !!state.assessmentProgress.byAssessment[assessmentUuid]?._checkpoint?.pausedAt;

/**
 * Get resume data for RiskAutopilotV2
 * Returns null if not paused, otherwise returns resume position data
 */
export const selectResumeData = (state, assessmentUuid) => {
  const checkpoint = state.assessmentProgress.byAssessment[assessmentUuid]?._checkpoint;
  if (!checkpoint?.pausedAt) return null;
  return {
    currentAreaIndex: checkpoint.currentAreaIndex,
    currentArea: checkpoint.currentArea,
    completedAreas: checkpoint.completedAreas || [],
    phase: checkpoint.phase,
    pausedAt: checkpoint.pausedAt,
    uploaded: checkpoint.uploaded,
    narrativesApproved: checkpoint.narrativesApproved,
    majorStep: checkpoint.majorStep
  };
};

export default assessmentProgressSlice.reducer;
